import cx from "classnames";
import { useLayoutEffect, useState } from "react";

import { ChevronRight } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import { showFullDescription } from "ds/components/FullDescriptionDrawer";
import Link from "ds/components/Link";
import useElementWidth from "hooks/useElementWidth";
import usePrevious from "hooks/usePrevious";

import ListEntitiesItemDescriptionContent from "./Content";
import styles from "./styles.module.css";

export type ListEntitiesItemDescriptionMultipleLinesProps =
  | {
      description: string;
      onShowFullDescription: (description: string) => void;
      className?: string;
      lines?: number;
      hasChevronLink?: boolean;
      actionText?: string;
      drawerTitle?: string;
    }
  | {
      description: string;
      onShowFullDescription?: never;
      className?: string;
      lines?: number;
      hasChevronLink?: boolean;
      actionText?: string;
      drawerTitle?: string;
    };

const ListEntitiesItemDescriptionMultipleLines = ({
  description,
  onShowFullDescription,
  className,
  lines = 3,
  hasChevronLink,
  actionText = "Show full description",
  drawerTitle,
}: ListEntitiesItemDescriptionMultipleLinesProps) => {
  const [textRef, containerSize] = useElementWidth<HTMLDivElement>();
  const [isTextEllipsis, setIsTextEllipsis] = useState(false);
  const prevWidth = usePrevious(containerSize);

  useLayoutEffect(() => {
    if (prevWidth !== containerSize && textRef.current) {
      setIsTextEllipsis(
        textRef.current.scrollHeight > textRef.current.getBoundingClientRect().height
      );
    }
  }, [prevWidth, containerSize, textRef]);

  const handleShowFullDescription = () => {
    showFullDescription({ description, title: drawerTitle });
    onShowFullDescription?.(description);
  };

  return (
    <Box
      grow="1"
      direction={hasChevronLink ? "row" : "column"}
      gap="small"
      align={hasChevronLink ? "center" : "start"}
      className={cx(styles.wrapper, styles.multipleLinesWrapper)}
    >
      <ListEntitiesItemDescriptionContent
        ref={textRef}
        description={description}
        style={{ WebkitLineClamp: lines }}
        className={cx(className, styles.multipleLines)}
        color="primary"
      />

      {isTextEllipsis && hasChevronLink && (
        <ButtonIcon variant="ghost" icon={ChevronRight} onPress={handleShowFullDescription}>
          {actionText}
        </ButtonIcon>
      )}

      {isTextEllipsis && !hasChevronLink && (
        <Link
          onPress={handleShowFullDescription}
          size="small"
          className={styles.showFullDescriptionLink}
        >
          {actionText}
        </Link>
      )}
    </Box>
  );
};

export default ListEntitiesItemDescriptionMultipleLines;
